import React, { useContext, useEffect, useState } from 'react';
import { Col, Row } from 'react-dsgov';
import styles from './formNCMReceitaExportacoes.module.scss';
import { BrButton, BrDivider, BrInput, BrLoading } from '@govbr-ds/react-components';
import { useNotificacao } from '../../../../../../../../../../../hooks/useNotificacao';
import { DiefCfemContext } from '../../../../../../../../../../../contexts/DiefCfemContext';
import ErrorMessage from '../../../../../../../../../../../components/errorMessage/errorMessage';
import AtualizarInformacoes from '../../../../../../../../atualizarInformacoes/atualizarInformacoes';
import AnmSwitch from '../../../../../../../../../../../components/anmSwitch/anmSwitch';
import { useQuery } from '@tanstack/react-query';
import { useBackEndRequests } from '../../../../../../../../../lib/backEndRequests';
import {
  IReceitaExportacoesNCM,
  IReceitaExportacoesNCMEnviar,
  IReceitaExportacoesVinculadaNCM,
  IReceitaExportacoesVinculadaNCMEnviar,
} from '../../../../../../../../../../../interfaces/IReceitaExportacoes';
import { formatCurrency, removeFormatCurrency } from '../../../../../../../../../../../utils/formatCurrency';
import AnmSelectQuery from '../../../../../../../../../../../components/anmSelectQuery/anmSelectQuery';
import { validarReceitaCalculadaExportacoes } from './formNCMReceitaExportacoesValidacoes';
import InfoCircleTooltip from '../../../../../../../../../../../components/infoCircleTooltip/infoCircleTooltip';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { ITributos } from 'interfaces/ITributos';
import SecaoDeducoes from 'components/secaoDeducoes/secaoDeducoes';
import { tratarMsgErroRequisicoesBack } from 'utils/tratarMsgErro';

interface IFormNCMReceitaExportacoesProps {
  refetchListaNCM: () => void;
  isEdicaoNcm: boolean;
  setIsEdicaoNcm?: React.Dispatch<React.SetStateAction<boolean>>;
  refetchDadosAnteriores?: () => void;
}

const FormNCMReceitaExportacoes = ({
  refetchListaNCM,
  isEdicaoNcm,
  setIsEdicaoNcm,
}: IFormNCMReceitaExportacoesProps) => {
  const context = useContext(DiefCfemContext);

  const [hasOperacaoVinculada, setHasOperacaoVinculada] = useState<boolean>(
    isEdicaoNcm ? context?.hasOperacaoVinculadaReceitaExportacoes || false : false
  );
  const [metodoApuracaoPrecoParametro, setMetodoApuracaoPrecoParametro] = useState<string>('');

  const [totalOperacao, setTotalOperacao] = useState<string>('');
  const [quantidade, setQuantidade] = useState<string>('');
  const [unidadeMedidaSelecionada, setUnidadeMedidaSelecionada] = useState<string>('');

  const [precoParametro, setPrecoParametro] = useState<string>('');

  const [valorProducaoTotal, setValorProducaoTotal] = useState<string>('');
  const [teorConcentrado, setTeorConcentrado] = useState<string>('');
  const [teorAlimentacao, setTeorAlimentacao] = useState<string>('');
  const [substanciaAjuste, setSubstanciaAjuste] = useState<string>('');

  const [codigoNCMSelecionado, setCodigoNCMSelecionado] = useState<string>('');

  const [haRejeito, setHaRejeito] = React.useState<boolean>(false);
  const [haUsoEspecifico, setHaUsoEspecifico] = React.useState<boolean>(false);

  const [haCompraTerceiro, setHaCompraTerceiro] = React.useState<boolean>(false);
  const [quantidadeTerceiro, setQuantidadeTerceiro] = React.useState<string>('');
  const [valorTotalTerceiro, setValorTotalTerceiro] = React.useState<string>('');

  const [isLoadingInfo, setIsLoadingInfo] = React.useState<boolean>(false);
  const [loadingBotao, setLoadingBotao] = React.useState<boolean>(false);

  const [infoSemOperacoes, setInfoSemOperacoes] = React.useState<
    { label: string; value: number; precoUtilizado?: string }[]
  >([
    { label: 'Preço Utilizado (R$)', value: 0, precoUtilizado: '' },
    { label: 'Receita Calculada (R$)', value: 0 },
    { label: 'Base de Cálculo (R$)', value: 0 },
  ]);

  const NAO_SE_APLICA = 'Não se aplica';

  const [infoComOperacoes, setInfoComOperacoes] = React.useState<
    { label: string; value: number | string | null; precoUtilizado?: string; format?: (value: number | null) => void }[]
  >([
    {
      label: 'Índice de Enriquecimento',
      value: null,
      format: () => formatarNullNaoSeAplica(),
    },
    {
      label: 'Fator de Ajuste',
      value: null,
      format: () => formatarNullNaoSeAplica(),
    },
    { label: 'Valor de Referência Unitário (R$)', value: null, format: () => formatarNullNaoSeAplica() },
    { label: 'Valor Utilizado (R$)', value: 0, precoUtilizado: '' },
    { label: 'Receita Calculada (R$)', value: 0 },
    { label: 'Base de Cálculo (R$)', value: 0 },
  ]);

  const [tributosAdicionados, setTributosAdicionados] = useState<
    {
      nome: string;
      valor: number;
    }[]
  >([]);

  const mostrarNotificacao = useNotificacao();

  const {
    getUnidadeMedidaReceitaExportacoes,
    getNCMReceitaExportacoes,
    getFatoresAjusteReceitaExportacoes,
    getMetodoApuracaoPrecos,
    postNCMReceitaExportacoes,
    putNCMReceitaExportacoes,
    postNCMVinculadaReceitaExportacoes,
    putNCMVinculadaReceitaExportacoes,
    getNCMAdicionadoReceitaExportacoes,
    getNCMVinculadaAdicionadoReceitaExportacoes,
    postReceberInformacoesNCMReceitaExportacoes,
    postReceberInformacoesNCMVinculadaReceitaExportacoes,
    getTributosReceitaExportacoes,
  } = useBackEndRequests();

  const {
    data: ncmAdicionadoSemVinculacaoData,
    isLoading: ncmAdicionadoSemVinculacaoDataLoading,
    error: ncmAdicionadoSemVinculacaoDataError,
  } = useQuery<IReceitaExportacoesNCM, IErrorWithSentryId>({
    queryKey: ['getNCMAdicionadoReceitaExportacoes', context?.idReceitaExportacoesNCM],
    queryFn: () => getNCMAdicionadoReceitaExportacoes(context?.idReceitaExportacoesNCM ?? -1),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled:
      isEdicaoNcm &&
      context?.idReceitaExportacoesNCM !== -1 &&
      context?.hasOperacaoVinculadaReceitaExportacoes === false,
  });

  const {
    data: ncmVinculadaAdicionadoData,
    isLoading: ncmVinculadaAdicionadoDataLoading,
    error: ncmVinculadaAdicionadoDataError,
  } = useQuery<IReceitaExportacoesVinculadaNCM, IErrorWithSentryId>({
    queryKey: ['getNCMVinculadaAdicionadoReceitaExportacoes', context?.idReceitaExportacoesNCM],
    queryFn: () => getNCMVinculadaAdicionadoReceitaExportacoes(context?.idReceitaExportacoesNCM ?? -1),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled:
      isEdicaoNcm &&
      context?.idReceitaExportacoesNCM !== -1 &&
      context?.hasOperacaoVinculadaReceitaExportacoes === true,
  });

  const {
    data: tributosPossiveisData,
    isLoading: tributosPossiveisLoading,
    error: tributosPossiveisError,
    refetch: refetchTributosPossiveis,
  } = useQuery<ITributos, IErrorWithSentryId>({
    queryKey: ['getTributosReceitaBruta'],
    queryFn: () => getTributosReceitaExportacoes(context?.idOperacaoEditar.toString() || ''),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const ncmAdicionadoData = context?.hasOperacaoVinculadaReceitaExportacoes
      ? ncmVinculadaAdicionadoData
      : ncmAdicionadoSemVinculacaoData;

    if (isEdicaoNcm && ncmAdicionadoData) {
      setHasOperacaoVinculada(ncmAdicionadoData?.hasExportacao);
      setCodigoNCMSelecionado(ncmAdicionadoData?.ncmBemMineral?.codigo);
      setTotalOperacao(formatCurrency(ncmAdicionadoData?.valorTotalOperacao));
      setQuantidade(ncmAdicionadoData?.quantidade?.toString());
      setUnidadeMedidaSelecionada(ncmAdicionadoData?.unidadeMedida);

      if (ncmAdicionadoData?.quantidadeAquisicaoTerceiros !== 0 && ncmAdicionadoData?.valorAquisicaoTerceiros !== 0) {
        setHaCompraTerceiro(true);
        setQuantidadeTerceiro(ncmAdicionadoData?.quantidadeAquisicaoTerceiros?.toString());
        setValorTotalTerceiro(formatCurrency(ncmAdicionadoData?.valorAquisicaoTerceiros));
      }

      setHaRejeito(ncmAdicionadoData?.isRejeito);
      setHaUsoEspecifico(ncmAdicionadoData?.hasUsoEspecifico);

      if (ncmAdicionadoData?.hasExportacao) {
        setMetodoApuracaoPrecoParametro(ncmVinculadaAdicionadoData?.apuracao?.metodo || '');
        setPrecoParametro(formatCurrency(ncmVinculadaAdicionadoData?.precoParametro || ''));
      } else if (!ncmAdicionadoData?.hasExportacao) {
        setValorProducaoTotal(formatCurrency(ncmAdicionadoSemVinculacaoData?.valorProducaoTotal || ''));
        setTeorConcentrado(ncmAdicionadoSemVinculacaoData?.teorConcentrado?.toString() || '');
        setTeorAlimentacao(ncmAdicionadoSemVinculacaoData?.teorAlimentacao?.toString() || '');
        setSubstanciaAjuste(
          ncmAdicionadoSemVinculacaoData?.substanciaFatorAjuste?.id
            ? ncmAdicionadoSemVinculacaoData?.substanciaFatorAjuste?.id.toString()
            : '-1'
        );
      }

      if (ncmAdicionadoData?.tributosValores) {
        setTributosAdicionados(ncmAdicionadoData.tributosValores);
      }
    }
  }, [isEdicaoNcm, ncmAdicionadoSemVinculacaoData, ncmVinculadaAdicionadoData]);

  const limparCampos = () => {
    setCodigoNCMSelecionado('');
    setPrecoParametro('');
    setValorProducaoTotal('');
    setTeorConcentrado('');
    setTeorAlimentacao('');
    substanciaAjuste !== '-1' && setSubstanciaAjuste('');
    setTotalOperacao('');
    setHasOperacaoVinculada(false);
    setQuantidade('');
    setHaRejeito(false);
    setHaUsoEspecifico(false);
    setHaCompraTerceiro(false);
    setQuantidadeTerceiro('');
    setValorTotalTerceiro('');
    setInfoComOperacoes([
      {
        label: 'Índice de Enriquecimento',
        value: null,
        format: () => formatarNullNaoSeAplica(),
      },
      {
        label: 'Fator de Ajuste',
        value: null,
        format: () => formatarNullNaoSeAplica(),
      },
      { label: 'Valor de Referência Unitário (R$)', value: null, format: () => formatarNullNaoSeAplica() },
      { label: 'Valor Utilizado (R$)', value: 0, precoUtilizado: '' },
      { label: 'Receita Calculada (R$)', value: 0 },
      { label: 'Base de Cálculo (R$)', value: 0 },
    ]);
    setInfoSemOperacoes([
      { label: 'Preço Utilizado (R$)', value: 0, precoUtilizado: '' },
      { label: 'Receita Calculada (R$)', value: 0 },
      { label: 'Base de Cálculo (R$)', value: 0 },
    ]);
    setMetodoApuracaoPrecoParametro('');
    setUnidadeMedidaSelecionada('');
    setTributosAdicionados([]);
  };

  const montagemObjetoItemEValidacao = () => {
    const ncm: IReceitaExportacoesNCMEnviar = {
      idReceitaCalculadaExportacoes: context?.idReceitaExportacoes ?? -1,
      codigoNcm: codigoNCMSelecionado,
      unidadeMedida: unidadeMedidaSelecionada,
      quantidade: parseFloat(quantidade) || 0,
      valorTotalOperacao: removeFormatCurrency(totalOperacao) || 0,
      valorProducaoTotal: removeFormatCurrency(valorProducaoTotal) || 0,
      teorConcentrado: parseFloat(teorConcentrado) || 0,
      teorAlimentacao: parseFloat(teorAlimentacao) || 0,
      quantidadeAquisicaoTerceiros: haCompraTerceiro ? parseFloat(quantidadeTerceiro) || 0 : 0,
      valorAquisicaoTerceiros: haCompraTerceiro ? removeFormatCurrency(valorTotalTerceiro) || 0 : 0,
      isRejeito: haRejeito,
      hasUsoEspecifico: haUsoEspecifico,
      hasExportacao: false,
      idSubstanciaFatorAjuste: substanciaAjuste === '-1' ? null : parseInt(substanciaAjuste),
      tributosValores: tributosAdicionados,
    };

    const msgErroValidacao = validarReceitaCalculadaExportacoes(
      ncm,
      haCompraTerceiro,
      tributosPossiveisData,
      substanciaAjuste
    );

    if (msgErroValidacao) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: msgErroValidacao,
      });
      return null;
    }
    return ncm;
  };

  const montagemObjetoItemVinculadaEValidacao = () => {
    const ncmVinculado: IReceitaExportacoesVinculadaNCMEnviar = {
      idReceitaCalculadaExportacoes: context?.idReceitaExportacoes ?? -1,
      codigoNcm: codigoNCMSelecionado,
      unidadeMedida: unidadeMedidaSelecionada,
      nomeMetodoApuracaoPreco: metodoApuracaoPrecoParametro,
      quantidade: parseFloat(quantidade) || 0,
      valorTotalOperacao: removeFormatCurrency(totalOperacao) || 0,
      precoParametro: removeFormatCurrency(precoParametro) || 0,
      quantidadeAquisicaoTerceiros: haCompraTerceiro ? parseFloat(quantidadeTerceiro) || 0 : 0,
      valorAquisicaoTerceiros: haCompraTerceiro ? removeFormatCurrency(valorTotalTerceiro) || 0 : 0,
      isRejeito: haRejeito,
      hasUsoEspecifico: haUsoEspecifico,
      hasExportacao: true,
      tributosValores: tributosAdicionados,
    };

    const msgErroValidacao = validarReceitaCalculadaExportacoes(ncmVinculado, haCompraTerceiro, tributosPossiveisData);

    if (msgErroValidacao) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: msgErroValidacao,
      });
      return null;
    }
    return ncmVinculado;
  };

  const navegarParaListaNCM = async () => {
    const buttonEdit = document.getElementById('lista-ncm-adicionado');

    if (buttonEdit) {
      window.scrollTo({
        top: buttonEdit.offsetTop,
      });
    }
  };

  const adicionarNCM = async () => {
    if (!hasOperacaoVinculada) {
      const ncm = montagemObjetoItemEValidacao();

      if (!ncm) return;

      setLoadingBotao(true);

      if (isEdicaoNcm) {
        const ncmComId = {
          ...ncm,
          id: context?.idReceitaExportacoesNCM ?? -1,
        };
        await putNCMReceitaExportacoes(ncmComId)
          .then(() => {
            limparCampos();
            refetchListaNCM();
            navegarParaListaNCM();
            if (setIsEdicaoNcm) setIsEdicaoNcm(false);
          })
          .catch((err) => {
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: tratarMsgErroRequisicoesBack(err, 'Erro ao enviar NCM, tente novamente.'),
              ...(err && { complementoSentryId: err }),
            });
          })
          .finally(() => {
            setLoadingBotao(false);
          });
      } else {
        const ncmComIdExportacoes = {
          ...ncm,
          idReceitaCalculadaExportacoes: context?.idReceitaExportacoes ?? -1,
        };
        await postNCMReceitaExportacoes(ncmComIdExportacoes)
          .then(() => {
            limparCampos();
            refetchListaNCM();
            navegarParaListaNCM();
          })
          .catch((err) => {
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: tratarMsgErroRequisicoesBack(err, 'Erro ao enviar NCM, tente novamente.'),
              ...(err && { complementoSentryId: err }),
            });
          })
          .finally(() => {
            setLoadingBotao(false);
          });
      }
    } else {
      const ncmVinculado = montagemObjetoItemVinculadaEValidacao();

      if (!ncmVinculado) return;

      setLoadingBotao(true);

      if (isEdicaoNcm) {
        const ncmVinculadoComId = {
          ...ncmVinculado,
          id: context?.idReceitaExportacoesNCM ?? -1,
        };
        await putNCMVinculadaReceitaExportacoes(ncmVinculadoComId)
          .then(() => {
            limparCampos();
            refetchListaNCM();
            navegarParaListaNCM();
            if (setIsEdicaoNcm) setIsEdicaoNcm(false);
          })
          .catch((err) => {
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: tratarMsgErroRequisicoesBack(err, 'Erro ao enviar NCM, tente novamente.'),
              ...(err && { complementoSentryId: err }),
            });
          })
          .finally(() => {
            setLoadingBotao(false);
          });
      } else {
        const ncmVinculadoComIdExportacoes = {
          ...ncmVinculado,
          idReceitaCalculadaExportacoes: context?.idReceitaExportacoes ?? -1,
        };
        await postNCMVinculadaReceitaExportacoes(ncmVinculadoComIdExportacoes)
          .then(() => {
            limparCampos();
            refetchListaNCM();
            navegarParaListaNCM();
          })
          .catch((err) => {
            mostrarNotificacao({
              tipo: 'danger',
              conteudo: tratarMsgErroRequisicoesBack(err, 'Erro ao enviar NCM, tente novamente.'),
              ...(err && { complementoSentryId: err }),
            });
          })
          .finally(() => {
            setLoadingBotao(false);
          });
      }
    }
  };

  const ncmAdicionadoDataLoading = context?.hasOperacaoVinculadaReceitaExportacoes
    ? ncmVinculadaAdicionadoDataLoading
    : ncmAdicionadoSemVinculacaoDataLoading;
  const ncmAdicionadoDataError = context?.hasOperacaoVinculadaReceitaExportacoes
    ? ncmVinculadaAdicionadoDataError
    : ncmAdicionadoSemVinculacaoDataError;

  const formatarFloat = (value: number) => {
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
  };

  const formatarNullNaoSeAplica = () => {
    return NAO_SE_APLICA;
  };

  const receberInformacoesBaseCalculo = async () => {
    setIsLoadingInfo(true);

    if (!hasOperacaoVinculada) {
      const ncm = montagemObjetoItemEValidacao();

      if (!ncm) {
        setIsLoadingInfo(false);
        return;
      }

      await postReceberInformacoesNCMReceitaExportacoes(ncm)
        .then((res) => {
          setInfoComOperacoes([
            {
              label: 'Índice de Enriquecimento',
              value: res?.indiceEnriquecimento,
              format: (value: number | null) => (value ? formatarFloat(value) : formatarNullNaoSeAplica()),
            },
            {
              label: 'Fator de Ajuste',
              value: res?.fatorAjuste,
              format: (value: number | null) => (value ? formatarFloat(value) : formatarNullNaoSeAplica()),
            },
            {
              label: 'Valor de Referência Unitário (R$)',
              value: res?.valorReferenciaUnitario,
              ...(res?.valorReferenciaUnitario == null && { format: () => formatarNullNaoSeAplica() }),
            },
            {
              label: 'Valor Utilizado (R$)',
              value: res?.valorUtilizado,
              precoUtilizado:
                res?.descricaoValorUtilizado === 'Praticado'
                  ? 'Preço ' + res?.descricaoValorUtilizado + ': '
                  : 'Valor ' + res?.descricaoValorUtilizado + ': ',
            },
            { label: 'Receita Calculada (R$)', value: res?.valorReceitaCalculada },
            ...(res?.deducoes > 0 ? [{ label: 'Deduções (R$)', value: res?.deducoes }] : []),
            { label: 'Base de Cálculo (R$)', value: res?.valorBaseCalculo },
          ]);
          setIsLoadingInfo(false);
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: tratarMsgErroRequisicoesBack(
              err,
              'Erro ao calcular a Base de Cálculo. Por favor, tente novamente.'
            ),
            ...(err && { complementoSentryId: err }),
          });
          setIsLoadingInfo(false);
        });
    } else {
      const ncmVinculado = montagemObjetoItemVinculadaEValidacao();

      if (!ncmVinculado) {
        setIsLoadingInfo(false);
        return;
      }

      await postReceberInformacoesNCMVinculadaReceitaExportacoes(ncmVinculado)
        .then((res) => {
          setInfoSemOperacoes([
            {
              label: 'Preço Utilizado (R$)',
              value: res?.precoUtilizado,
              precoUtilizado: 'Preço ' + res?.descricaoPrecoUtilizado + ': ',
            },
            { label: 'Receita Calculada (R$)', value: res?.valorReceitaCalculada },
            { label: 'Base de Cálculo (R$)', value: res?.valorBaseCalculo },
          ]);
          setIsLoadingInfo(false);
        })
        .catch((err) => {
          mostrarNotificacao({
            tipo: 'danger',
            conteudo: tratarMsgErroRequisicoesBack(
              err,
              'Erro ao calcular a Base de Cálculo. Por favor, tente novamente.'
            ),
            ...(err && { complementoSentryId: err }),
          });

          setIsLoadingInfo(false);
        });
    }
  };

  const formatResultFatorAjuste = (result: any) => {
    const options = [];

    if (result?.fatoresAjuste && result?.fatoresAjuste.length > 0) {
      const resultOptions = result?.fatoresAjuste.map((fator: any) => ({
        label: fator.nomeSubstancia,
        value: fator.substanciaCorrelacionadaId,
      }));
      options.push(...resultOptions);
      setSubstanciaAjuste('');
    } else {
      options.push({ label: 'Não se aplica', value: '-1' });
      setSubstanciaAjuste('-1');
    }

    return options;
  };

  const htmlTributos = () => {
    if (tributosPossiveisLoading) return <BrLoading />;
    if (tributosPossiveisError) {
      return (
        <div className={styles.errorContainer}>
          <span className={styles.errorText}>Não foi possível atualizar a lista de tributos</span>
          <BrButton size='small' onClick={() => refetchTributosPossiveis()}>
            (Tentar novamente)
          </BrButton>
        </div>
      );
    }

    return (
      <SecaoDeducoes
        key={codigoNCMSelecionado}
        deducoesPossiveis={tributosPossiveisData}
        deducoesAdicionadas={tributosAdicionados}
        setDeducoesAdicionadas={setTributosAdicionados}
        semMargem
        valorReceitaBruta={totalOperacao}
        isApresentarSecaoCompraTerceiros
        haCompraTerceiro={haCompraTerceiro}
        setHaCompraTerceiro={setHaCompraTerceiro}
        quantidadeCompraTerceiro={quantidadeTerceiro}
        setQuantidadeCompraTerceiro={setQuantidadeTerceiro}
        valorCompraTerceiro={valorTotalTerceiro}
        setValorCompraTerceiro={setValorTotalTerceiro}
      />
    );
  };

  return (
    <div>
      <div className={styles.title}>{isEdicaoNcm ? 'Edite NCM' : 'Adicione um NCM'}</div>
      {ncmAdicionadoDataError && isEdicaoNcm && (
        <ErrorMessage errorMessage={ncmAdicionadoDataError?.message} error={ncmAdicionadoDataError} />
      )}
      {isEdicaoNcm && ncmAdicionadoDataLoading ? (
        <BrLoading />
      ) : (
        <div className={styles.content}>
          <div className={styles.form}>
            {!isEdicaoNcm ? (
              <AnmSwitch
                switchDataTestId='switch-has-operacao-vinculada'
                label=' Há operação de exportação com Pessoa Vinculada/Interposta/País com Tributação Favorecida?'
                value={hasOperacaoVinculada}
                setValue={setHasOperacaoVinculada}
              >
                <AnmSelectQuery
                  id='select-metodo-apuracao-preco'
                  label={'Método para Apuração do Preço'}
                  value={metodoApuracaoPrecoParametro}
                  setValue={setMetodoApuracaoPrecoParametro}
                  functionQuery={() => getMetodoApuracaoPrecos()}
                  keyQuery={['getMetodosApuracaoPreco']}
                  formatResult={(result: any) =>
                    result.map((item: any) => ({
                      label: item,
                      value: item,
                    }))
                  }
                  placeholder={metodoApuracaoPrecoParametro || 'Selecione o método'}
                />
              </AnmSwitch>
            ) : (
              <Row>
                <Col md={4} sm={8}>
                  <span className={styles.label}>
                    Há operação de exportação com Pessoa Vinculada/Interposta/País com Tributação Favorecida?
                  </span>
                </Col>
                <Col md={4} sm={4}>
                  <span>{hasOperacaoVinculada ? 'Sim' : 'Não'}</span>
                </Col>
                <Col md={4} sm={12}>
                  {hasOperacaoVinculada && (
                    <AnmSelectQuery
                      id='select-metodo-apuracao-preco'
                      label={'Método para Apuração do Preço'}
                      value={metodoApuracaoPrecoParametro}
                      setValue={setMetodoApuracaoPrecoParametro}
                      functionQuery={() => getMetodoApuracaoPrecos()}
                      keyQuery={['getMetodosApuracaoPreco']}
                      formatResult={(result: any) =>
                        result.map((item: any) => ({
                          label: item,
                          value: item,
                        }))
                      }
                      placeholder={metodoApuracaoPrecoParametro || 'Selecione o método'}
                    />
                  )}
                </Col>
              </Row>
            )}

            <BrDivider className={styles.divider} dashed />
            <Row>
              <Col>
                <AnmSelectQuery
                  id='select-codigo-ncm'
                  label={'Código NCM'}
                  value={codigoNCMSelecionado}
                  setValue={setCodigoNCMSelecionado}
                  functionQuery={() => getNCMReceitaExportacoes()}
                  keyQuery={['getNCMReceitaExportacoes']}
                  formatResult={(result: any) =>
                    result.map((ncm: any) => ({
                      label: `${ncm.codigo} - ${ncm.descricao}`,
                      value: ncm.codigo,
                    }))
                  }
                />
              </Col>
              <Col>
                <span className={styles.label}>Total da Operação (R$)</span>
                <BrInput
                  data-testid='input-total-operacao'
                  className={styles.input}
                  value={totalOperacao}
                  onChange={(e) => setTotalOperacao(e.target.value)}
                  numeric={{
                    prefix: 'R$ ',
                    thousandSeparator: '.',
                    decimalScale: 2,
                    fixedDecimalScale: false,
                  }}
                />
              </Col>
              <Col>
                <span className={styles.label}>Quantidade</span>
                <BrInput
                  data-testid='input-quantidade'
                  className={styles.input}
                  value={quantidade}
                  onChange={(e) => setQuantidade(e.target.value.replace(',', '.'))}
                  numeric={{
                    decimalScale: 2,
                    fixedDecimalScale: false,
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <AnmSelectQuery
                  id='select-unidade-medida'
                  label={'Unidade de Medida'}
                  value={unidadeMedidaSelecionada}
                  setValue={setUnidadeMedidaSelecionada}
                  functionQuery={() => getUnidadeMedidaReceitaExportacoes()}
                  keyQuery={['getUnidadeMedidaReceitaExportacoes']}
                />
              </Col>
              <Col className={styles.dataContainer}>
                <span className={styles.label}>Preço Praticado (R$)</span>
                <span>
                  {totalOperacao && quantidade && quantidade !== '0'
                    ? formatCurrency(removeFormatCurrency(totalOperacao) / parseFloat(quantidade))
                    : 'R$ 00,00'}
                </span>
              </Col>
              {hasOperacaoVinculada ? (
                <Col>
                  <span className={styles.label}>Preço Parâmetro (R$)</span>
                  <BrInput
                    data-testid='input-preco-parametro'
                    className={styles.input}
                    value={precoParametro}
                    onChange={(e) => setPrecoParametro(e.target.value)}
                    numeric={{
                      prefix: 'R$ ',
                      thousandSeparator: '.',
                      decimalScale: 2,
                      fixedDecimalScale: false,
                    }}
                  />
                </Col>
              ) : (
                <Col>
                  <div className={styles.tooltipLabel}>
                    <div className={styles.label}>
                      Valor de Produção (VP){' '}
                      <div className={styles.tooltip}>
                        <InfoCircleTooltip
                          text='Informe o Valor de Produção (VP) proporcional à quantidade de bem mineral vinculada à operação que está sendo declarada, apurado nos termos do art. 2º, I, do Decreto nº 9.252/2017.'
                          id='tooltipVP'
                        />
                      </div>
                    </div>
                  </div>
                  <BrInput
                    aria-label='Valor de Produção (VP)'
                    data-testid='input-valor-producao'
                    className={styles.input}
                    value={valorProducaoTotal}
                    onChange={(e) => setValorProducaoTotal(e.target.value)}
                    numeric={{
                      prefix: 'R$ ',
                      thousandSeparator: '.',
                      decimalScale: 2,
                      fixedDecimalScale: false,
                    }}
                  />
                </Col>
              )}
            </Row>
            {!hasOperacaoVinculada && (
              <Row>
                <Col>
                  <span className={styles.label}>% Teor Concentrado (TC)</span>
                  <BrInput
                    data-testid='input-teor-concentrado'
                    className={styles.input}
                    value={teorConcentrado}
                    onChange={(e) => setTeorConcentrado(e.target.value.replace(',', '.'))}
                    numeric={{
                      decimalScale: 2,
                      fixedDecimalScale: false,
                    }}
                  />
                </Col>
                <Col>
                  <span className={styles.label}>% Teor Alimentação (TA)</span>
                  <BrInput
                    data-testid='input-teor-alimentacao'
                    className={styles.input}
                    value={teorAlimentacao}
                    onChange={(e) => setTeorAlimentacao(e.target.value.replace(',', '.'))}
                    numeric={{
                      decimalScale: 2,
                      fixedDecimalScale: false,
                    }}
                  />
                </Col>
                <Col>
                  <AnmSelectQuery
                    id='select-substancia-fator-ajuste'
                    label={'Substância (Fatores de Ajuste)'}
                    value={substanciaAjuste}
                    setValue={setSubstanciaAjuste}
                    functionQuery={() => getFatoresAjusteReceitaExportacoes(context?.idOperacaoEditar || '')}
                    keyQuery={['getFatoresAjusteReceitaExportacoes', context?.idOperacaoEditar]}
                    formatResult={formatResultFatorAjuste}
                    placeholder={substanciaAjuste === '-1' ? 'Não se aplica' : 'Selecione a substância'}
                  />
                </Col>
              </Row>
            )}
          </div>
          {htmlTributos()}
          <div className={styles.otherContainer}>
            <div className={styles.atualizarInformacoesContainerFull}>
              <AtualizarInformacoes
                onClickFunction={() => receberInformacoesBaseCalculo()}
                info={!hasOperacaoVinculada ? infoComOperacoes : infoSemOperacoes}
                isLoading={isLoadingInfo}
              />
            </div>
          </div>

          <div className={styles.form}>
            <AnmSwitch
              label='O bem mineral é oriundo de rejeitos e estéreis de minerais associados utilizados em outras cadeias
                  produtivas?'
              value={haRejeito}
              setValue={setHaRejeito}
              tooltipText='Informação vinculada à redução da alíquota da CFEM em 50%, conforme § 7º, do Art. 6º, da Lei nº 7.990/1989. Os procedimentos para aproveitamento de rejeitos e estéreis estão dispostos na RESOLUÇÃO ANM Nº 85, DE 2 DE DEZEMBRO DE 2021.'
            />
            <AnmSwitch
              label='A substância possui uso específico?'
              value={haUsoEspecifico}
              setValue={setHaUsoEspecifico}
              tooltipText='Essa marcação só deve ser feita quando o bem mineral for destinado ao uso imediato na construção civil ou se enquadrar na categoria de rochas ornamentais, para fins de aplicação da alíquota de 1% (um por cento), nos termos do ANEXO da Lei nº 8.001/1990.'
            />
          </div>
          <div className={styles.buttonContainer}>
            {isEdicaoNcm === true && (
              <BrButton
                data-testid='btn-salvar-ncm'
                secondary
                size='small'
                icon='fas fa-save'
                onClick={() => {
                  adicionarNCM();
                }}
                loading={loadingBotao}
              >
                Salvar NCM
              </BrButton>
            )}
            {isEdicaoNcm === false && (
              <BrButton
                data-testid='btn-incluir-ncm'
                secondary
                size='small'
                icon='fas fa-plus'
                onClick={() => {
                  adicionarNCM();
                }}
                loading={loadingBotao}
              >
                Incluir NCM
              </BrButton>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FormNCMReceitaExportacoes;
